// frontend/src/components/common/Layout/Header.tsx

import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Button } from '@/components/common/UI/Button'
import { cn } from '@/utils/helpers'
import { SUPPORTED_LANGUAGES, changeLanguage } from '@/i18n'
import { useTranslation } from 'react-i18next'

export const Header: React.FC = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()
  const { t, i18n } = useTranslation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiClient.get<{ unread_count: number }>('/v1/notifications/unread/count'),
    enabled: isAuthenticated,
    refetchInterval: 60_000,
  })
  const unreadCount = unreadData?.unread_count || 0

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      )}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-xl font-bold" aria-label="LenJob — главная">
            <img src="/lenjob-logo.svg" alt="" className="h-9 w-9" />
            <span className="text-secondary-900">Len<span className="text-primary-600">Job</span></span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/vacancies" className="text-secondary-600 hover:text-primary-600 transition-colors">
              {t('nav.vacancies', 'Вакансии')}
            </Link>
            {isAuthenticated && user?.role === 'employer' && (
              <Link to="/resumes" className="text-secondary-600 hover:text-primary-600 transition-colors">
                {t('nav.resumes', 'Резюме')}
              </Link>
            )}
            <Link to="/companies" className="text-secondary-600 hover:text-primary-600 transition-colors">
              {t('nav.companies', 'Компании')}
            </Link>
            <Link to="/articles" className="text-secondary-600 hover:text-primary-600 transition-colors">Статьи</Link>
            <Link to="/services" className="text-secondary-600 hover:text-primary-600 transition-colors">{t('nav.services', 'Сервисы')}</Link>
            <Link to="/subscriptions" className="text-secondary-600 hover:text-primary-600 transition-colors">Тарифы</Link>
            {isAuthenticated && user?.role === 'admin' && <Link to="/admin" className="font-semibold text-primary-700 hover:text-primary-800">Админ-панель</Link>}
            <Link to="/help" className="text-secondary-600 hover:text-primary-600 transition-colors">
              {t('nav.help', 'Помощь')}
            </Link>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <div className="hidden items-center gap-1 lg:flex" aria-label="Поделиться в социальных сетях">
              <a href="https://vk.com/share.php?url=https%3A%2F%2Flenjob.ru" target="_blank" rel="noreferrer" aria-label="Поделиться во ВКонтакте" className="grid h-8 w-8 place-items-center rounded-full text-xs font-bold text-secondary-600 hover:bg-secondary-100">VK</a>
              <a href="https://t.me/share/url?url=https%3A%2F%2Flenjob.ru&text=LenJob" target="_blank" rel="noreferrer" aria-label="Поделиться в Telegram" className="grid h-8 w-8 place-items-center rounded-full text-secondary-600 hover:bg-secondary-100"><svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d="M21.5 3.4 18.2 20c-.2 1.2-.9 1.5-1.8.9l-5-3.7-2.4 2.3c-.3.3-.5.5-1 .5l.4-5.2 9.5-8.6c.4-.4-.1-.6-.6-.2L5.6 13.4.7 11.9c-1.1-.3-1.1-1.1.2-1.6L20 2.8c.9-.3 1.7.2 1.5.6Z"/></svg></a>
              <a href="https://max.ru/" target="_blank" rel="noreferrer" aria-label="LenJob в MAX" className="grid h-8 min-w-8 place-items-center rounded-full px-2 text-xs font-bold text-secondary-600 hover:bg-secondary-100">MAX</a>
            </div>
            <label className="sr-only" htmlFor="site-language">Язык интерфейса</label>
            <select id="site-language" aria-label="Язык интерфейса" value={i18n.resolvedLanguage || 'ru'} onChange={event => void changeLanguage(event.target.value)} className="max-w-32 rounded-lg border border-secondary-200 bg-white px-2 py-2 text-sm text-secondary-700">
              {Object.entries(SUPPORTED_LANGUAGES).map(([code, language]) => <option key={code} value={code}>{language.flag} {language.nativeName}</option>)}
            </select>
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Link to="/notifications" aria-label={`${t('nav.notifications', 'Уведомления')}, ${unreadCount} ${t('notification.unread', 'непрочитанных')}`} title={t('nav.notifications', 'Уведомления')} className="relative grid h-10 w-10 place-items-center rounded-full text-secondary-600 transition hover:bg-secondary-100 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></svg>
                  {unreadCount > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">{unreadCount > 99 ? '99+' : unreadCount}</span>}
                </Link>
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm">
                    {user?.first_name || t('nav.profile', 'Профиль')}
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  {t('nav.logout', 'Выйти')}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                  {t('nav.login', 'Войти')}
                </Button>
                <Button size="sm" onClick={() => navigate('/register')}>
                  {t('nav.register', 'Регистрация')}
                </Button>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-secondary-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={isMobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-secondary-100">
            <nav className="flex flex-col gap-3">
              <Link
                to="/vacancies"
                className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.vacancies', 'Вакансии')}
              </Link>
              {isAuthenticated && user?.role === 'employer' && (
                <Link
                  to="/resumes"
                  className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav.resumes', 'Резюме')}
                </Link>
              )}
              <Link
                to="/companies"
                className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.companies', 'Компании')}
              </Link>
              <Link to="/articles" className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1" onClick={() => setIsMobileMenuOpen(false)}>Статьи</Link>
              <Link to="/services" className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.services', 'Сервисы')}</Link>
              <Link to="/subscriptions" className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1" onClick={() => setIsMobileMenuOpen(false)}>Подписки и тарифы</Link>
              <div className="mt-2 flex gap-3 border-t border-secondary-100 px-2 pt-3">
                <a href="https://vk.com/share.php?url=https%3A%2F%2Flenjob.ru" target="_blank" rel="noreferrer" className="text-sm font-medium text-primary-700">VK · Поделиться</a>
                <a href="https://t.me/share/url?url=https%3A%2F%2Flenjob.ru&text=LenJob" target="_blank" rel="noreferrer" className="text-sm font-medium text-primary-700">Telegram · Поделиться</a>
                <a href="https://max.ru/" target="_blank" rel="noreferrer" className="text-sm font-medium text-primary-700">MAX</a>
              </div>
              {isAuthenticated && user?.role === 'admin' && <Link to="/admin" className="font-semibold text-primary-700 px-2 py-1" onClick={() => setIsMobileMenuOpen(false)}>Админ-панель</Link>}
              <Link
                to="/help"
                className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.help', 'Помощь и FAQ')}
              </Link>
              {isAuthenticated && (
                <Link
                  to="/dashboard"
                  className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Личный кабинет
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
